import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckCircle2, Circle, Eye, Search } from "@/components/icons"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type MockCitation = {
  id: string
  fileName: string
  library: string
}

const MOCK_CITATIONS: MockCitation[] = [
  { id: "1", fileName: "personalhandbok-2024.pdf", library: "HR Policyer" },
  { id: "2", fileName: "reseersättning-riktlinjer.pdf", library: "HR Policyer" },
  { id: "3", fileName: "budgetprocess-Q1.xlsx", library: "Ekonomi" },
  { id: "4", fileName: "it-säkerhetspolicy.pdf", library: "IT & Säkerhet" },
]

const ALL_CITATION_IDS = MOCK_CITATIONS.map((c) => c.id)

export function DemoActiveCitations() {
  const { t } = useTranslation()
  const [activeCitations, setActiveCitations] = useState<string[]>(["1", "3"])
  const [searchQuery, setSearchQuery] = useState("")
  const normalizedQuery = searchQuery.trim().toLowerCase()

  const citationsByLibrary = useMemo(() => {
    return MOCK_CITATIONS.reduce<Map<string, MockCitation[]>>((acc, citation) => {
      const existing = acc.get(citation.library) ?? []
      acc.set(citation.library, [...existing, citation])
      return acc
    }, new Map())
  }, [])

  const filteredCitationsByLibrary = useMemo(() => {
    if (!normalizedQuery) return citationsByLibrary

    const filtered = new Map<string, MockCitation[]>()
    citationsByLibrary.forEach((citations, library) => {
      const matchesLibrary = library.toLowerCase().includes(normalizedQuery)
      const matchingCitations = matchesLibrary
        ? citations
        : citations.filter((c) => c.fileName.toLowerCase().includes(normalizedQuery))

      if (matchingCitations.length > 0) {
        filtered.set(library, matchingCitations)
      }
    })
    return filtered
  }, [citationsByLibrary, normalizedQuery])

  const filteredCitationIds = useMemo(() => {
    const ids: string[] = []
    filteredCitationsByLibrary.forEach((citations) => {
      citations.forEach((c) => ids.push(c.id))
    })
    return ids
  }, [filteredCitationsByLibrary])

  const activeCount = activeCitations.filter((id) => ALL_CITATION_IDS.includes(id)).length
  const totalCount = MOCK_CITATIONS.length
  const filteredCount = filteredCitationIds.length

  const activeSet = useMemo(() => new Set(activeCitations), [activeCitations])
  const canSelectAll = filteredCitationIds.some((id) => !activeSet.has(id))
  const canClear = filteredCitationIds.some((id) => activeSet.has(id))

  const toggleCitation = (citationId: string) => {
    setActiveCitations((current) =>
      current.includes(citationId)
        ? current.filter((id) => id !== citationId)
        : [...current, citationId]
    )
  }

  const getLibraryCounts = (library: string) => {
    const libraryCitations = filteredCitationsByLibrary.get(library) ?? []
    const total = libraryCitations.length
    const active = libraryCitations.filter((c) => activeCitations.includes(c.id)).length
    return { total, active }
  }

  const toggleLibrary = (library: string) => {
    const libraryCitations = filteredCitationsByLibrary.get(library) ?? []
    const libraryIds = libraryCitations.map((c) => c.id)
    const allActive = libraryIds.every((id) => activeCitations.includes(id))

    if (allActive) {
      setActiveCitations((current) => current.filter((id) => !libraryIds.includes(id)))
    } else {
      setActiveCitations((current) => [
        ...current,
        ...libraryIds.filter((id) => !current.includes(id)),
      ])
    }
  }

  const handleSelectAll = () => {
    const merged = new Set(activeCitations)
    filteredCitationIds.forEach((id) => merged.add(id))
    setActiveCitations(Array.from(merged))
  }

  const handleClear = () => {
    const filteredSet = new Set(filteredCitationIds)
    setActiveCitations(activeCitations.filter((id) => !filteredSet.has(id)))
  }

  const countBadgeClassName = cn(
    "shrink-0 font-semibold",
    activeCount > 0
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-border bg-muted/40 text-muted-foreground"
  )

  return (
    <div className="bg-card flex w-full flex-col gap-3 rounded-lg border p-3 sm:max-w-sm sm:p-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t("settingsmenu.active_citations", "Active Citations")}
          </span>
          <p className="text-muted-foreground text-xs">
            {t("settingsmenu.active_citations_description", "Select sources to include in answers")}
          </p>
        </div>
        <Badge className={countBadgeClassName} variant="outline">
          {activeCount}/{totalCount}
        </Badge>
      </div>

      {/* Search and controls */}
      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            className="h-9 pl-9"
            placeholder={t("settingsmenu.active_citations_search_placeholder", "Search...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-muted-foreground text-xs">
            {t("settingsmenu.active_citations_showing_count", {
              shown: filteredCount,
              total: totalCount,
              defaultValue: `Showing ${filteredCount} of ${totalCount}`,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              disabled={!canSelectAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={handleSelectAll}
            >
              {t("settingsmenu.active_citations_select_all", "Select All")}
            </Button>
            <Button
              disabled={!canClear}
              size="sm"
              type="button"
              variant="ghost"
              onClick={handleClear}
            >
              {t("settingsmenu.active_citations_clear", "Clear")}
            </Button>
          </div>
        </div>
      </div>

      {/* Accordion list */}
      {filteredCount === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("settingsmenu.active_citations_no_results", "No results")}
        </p>
      ) : (
        <Accordion
          className="flex flex-col gap-3"
          defaultValue={Array.from(filteredCitationsByLibrary.keys())}
          type="multiple"
        >
          {Array.from(filteredCitationsByLibrary.entries()).map(([library, citations]) => {
            const counts = getLibraryCounts(library)
            const isAllActive = counts.total > 0 && counts.active === counts.total
            const actionLabel = isAllActive
              ? t("settingsmenu.active_citations_clear", "Clear")
              : t("settingsmenu.active_citations_select_all", "Select All")

            return (
              <AccordionItem
                key={library}
                className="border-border bg-background rounded-lg border border-b-0 px-3 py-2"
                value={library}
              >
                <AccordionTrigger className="[&>svg]:text-muted-foreground items-start gap-2 py-2 hover:no-underline [&>svg]:mt-1 [&>svg]:size-4 [&>svg]:shrink-0">
                  {/* Library checkbox with hover action */}
                  <div className="group/library flex min-w-0 flex-1 items-start justify-between gap-2">
                    <div
                      className={cn(
                        "min-w-0 flex-1 pr-0 transition-[padding]",
                        counts.total > 0 && "group-hover/library:pr-[44px]"
                      )}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Label className="line-clamp-2 block text-sm leading-snug font-medium">
                            {library}
                          </Label>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>{library}</p>
                        </TooltipContent>
                      </Tooltip>
                      <span className="text-muted-foreground text-xs">
                        {counts.active} of {counts.total} selected
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center">
                      <button
                        aria-label={actionLabel}
                        title={actionLabel}
                        type="button"
                        className={cn(
                          "relative z-10 h-6 w-[52px] shrink-0 justify-center overflow-hidden rounded-full border px-2 text-[11px] leading-none font-semibold transition-[width,margin-left,color,background-color,border-color]",
                          counts.active > 0
                            ? "border-primary/30 bg-primary/10 text-primary"
                            : "border-border bg-muted/40 text-muted-foreground",
                          counts.total > 0 &&
                            "group-hover/library:border-primary/40 group-hover/library:bg-primary/15 group-hover/library:text-primary group-hover/library:-ml-[44px] group-hover/library:w-[96px]"
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleLibrary(library)
                        }}
                      >
                        <span className="relative flex h-full w-full items-center justify-center">
                          <span className="absolute inset-0 flex items-center justify-center transition-opacity group-hover/library:opacity-0">
                            <span className="tabular-nums">
                              {counts.active}/{counts.total}
                            </span>
                          </span>
                          <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/library:opacity-100">
                            <span className="truncate px-1 text-[10px]">{actionLabel}</span>
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-2">
                  {/* Citation checkboxes */}
                  <section className="border-sidebar-border border-l pl-3">
                    <div className="flex flex-col gap-1.5">
                      {citations.map((citation) => {
                        const isActive = activeCitations.includes(citation.id)
                        return (
                          <div
                            key={citation.id}
                            className={cn(
                              "group/citation flex items-center gap-2 rounded-md px-2 py-1 transition-colors",
                              isActive ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50"
                            )}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  aria-pressed={isActive}
                                  className="min-w-0 flex-1 truncate text-left text-sm"
                                  type="button"
                                  onClick={() => toggleCitation(citation.id)}
                                >
                                  {citation.fileName}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <p>{citation.fileName}</p>
                              </TooltipContent>
                            </Tooltip>
                            <div className="flex items-center">
                              {/* Eye dropdown - visible on hover */}
                              <div
                                className={cn(
                                  "pointer-events-none max-w-0 overflow-hidden opacity-0 transition-[margin,max-width,opacity]",
                                  "group-hover/citation:pointer-events-auto group-hover/citation:mr-1 group-hover/citation:max-w-[64px] group-hover/citation:opacity-100"
                                )}
                              >
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      aria-label={t("answer.show_document", "Show document")}
                                      className="h-7 w-7"
                                      size="icon"
                                      variant="ghost"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent side="right">
                                    <DropdownMenuItem>
                                      <Eye className="h-4 w-4" />
                                      <span>{t("answer.show_document", "Show document")}</span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              {/* Checkbox icon - visible on hover or when active */}
                              <Button
                                aria-label={citation.fileName}
                                aria-pressed={isActive}
                                size="icon"
                                type="button"
                                variant="ghost"
                                className={cn(
                                  "h-7 overflow-hidden p-0 transition-[margin,max-width,opacity]",
                                  isActive
                                    ? "text-primary ml-1 max-w-7 opacity-100"
                                    : "text-muted-foreground ml-0 max-w-0 opacity-0 group-hover/citation:ml-1 group-hover/citation:max-w-7 group-hover/citation:opacity-100"
                                )}
                                onClick={() => toggleCitation(citation.id)}
                              >
                                {isActive ? (
                                  <CheckCircle2 className="size-4" />
                                ) : (
                                  <Circle className="size-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </section>
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      )}
    </div>
  )
}
